import {
  clearFilterCountCache,
  deleteFilter,
  getSavedFilters,
  saveFilter,
  updateFilter,
} from "@/store/services/filter/filterService";
import { RootState, filter, filterState } from "@/types";
import Notify from 'quasar/src/plugins/Notify.js';;
import { Module } from "vuex";
import store from "@/store";
import i18n from "@/i18n";

const filterModule: Module<filterState, RootState> = {
    namespaced: true,
    state: {
        listOfFilters: [],
        ShowFilter: false,
        showEditMode: false,
        isUpdateButton: false,
        selectedFilter: undefined,
        isLoading: false,
        lastLoadTimestamp: 0
    },
    getters: {
        getFilterInit(): filter {
            return {
                description: '',
                hasPublicAccess: false,
                numOrder: 1,
                screenId: '',
                count: 0,
                color: '',
                businessData: {},
                objectType: "odm.configuration.filterstore",
                systemUid: "odm-configuration"
            }
        },
        listOfFilters(state) {
            return state.listOfFilters;
        },
        showFilter(state) {
            return state.ShowFilter;
        },
        getSelectedFilter(state) {
            return state.selectedFilter;
        },
        getIsUpdateButton(state) {
            return state.isUpdateButton;
        },
        getIsLoading(state){
            return state.isLoading;
        }
    },
    actions: {
        showFilters({commit}) {
            commit('setShowListOfFilter', true)
        },
        disappearFilters({commit}) {
            commit('setShowListOfFilter', false)
        },
        showFilter({commit}) {
            commit('setShowFilter', true)
        },
        disappearFilter({commit}) {
            commit('setShowFilter', false)
        },
        setSelectedFilter({commit}, payload) {
            commit('setSelectedFilter', payload)
        },
        editSelectedFilter({commit}, payload) {
            commit('editSelectedFilter', payload)
        },
        async addFilter({dispatch}, payload) {
            if (!payload.resourceUid) {
                await saveFilter(payload)
                    .then(() => {
                        Notify.create({
                            message: i18n.global.t("main.filter.notifications.saveSuccess"),
                            color: "positive",
                            icon: "check_circle",
                            timeout: 20000,
                        });
                    })
                    .catch((err) => {
                        console.error(err);
                        Notify.create({
                            message: err,
                            color: "negative",
                        });
                    });
            } else{
                await updateFilter(payload)
                    .then(() => {
                        Notify.create({
                            message: i18n.global.t("main.filter.notifications.updateSuccess"),
                            color: "positive",
                            icon: "check_circle",
                            timeout: 20000,
                        });
                    })
                    .catch((err) => {
                        const message = err.message; 
                        Notify.create({
                            message,
                            color: "negative",
                        });
                    });

            }
            await dispatch("getSavedFilters", { queryId: payload.screenId });       
        },
        async getSavedFilters({commit, state}, payload) {
            const {queryId} = payload;
            const now = Date.now();
            
     
            const lastLoadKey = `lastLoad_${queryId}`;
            const stateAny = state as any;
            
            if (stateAny[lastLoadKey] && now - stateAny[lastLoadKey] < 500) {
                console.log(`[FilterModule] Throttled: ${queryId} was loaded ${now - stateAny[lastLoadKey]}ms ago`);
                return; 
            }
            
            stateAny[lastLoadKey] = now;
            commit("setIsLoading", true);

            try {
                const filters = await getSavedFilters(queryId);
                const filtersWithScreenId = filters.map((filter: any) => ({
                    ...filter,
                    screenId: filter.screenId || queryId 
                }));
                commit("setListOfFilters", filtersWithScreenId);
            } catch (err: any) {
                Notify.create({
                    message: err.message ?? err,
                    color: "negative",
                });
            } finally {
                commit("setIsLoading", false);
            }
        },
    async deleteSelectedFilter({ dispatch }, payload) {
      await deleteFilter(payload)
        .then(() => {
          dispatch("getSavedFilters", { queryId: payload.screenId });
          store.dispatch("hideConfirmationDeleteDialog");
          Notify.create({
            message: i18n.global.t("main.dialog.validation.success"),
            color: "positive",
          });
        })
        .catch((err) => {
          Notify.create({
            message: err,
            color: "negative",
          });
        });
     },
        resetFilterState({ commit }) {
            commit("resetState");
        }
    },
    mutations: {
        setShowFilter(state, payload) {
            state.ShowFilter = payload
        },
        setListOfFilters(state, payload) {
            state.listOfFilters = payload
        },
        upsertFilter(state, payload) {
            if (!payload || !payload.resourceUid) {
                return;
            }
            const list = state.listOfFilters || [];
            const index = list.findIndex((filter: any) => filter.resourceUid === payload.resourceUid);
            if (index === -1) {
                const insertIndex = list.findIndex((filter: any) => {
                    if (payload.numOrder === undefined || payload.numOrder === null) {
                        return false;
                    }
                    if (filter.numOrder === undefined || filter.numOrder === null) {
                        return false;
                    }
                    return filter.numOrder > payload.numOrder;
                });
                if (insertIndex === -1) {
                    list.push(payload);
                } else {
                    list.splice(insertIndex, 0, payload);
                }
            } else {
                list.splice(index, 1, {
                    ...list[index],
                    ...payload,
                });
            }
            state.listOfFilters = [...list];
        },
        removeFilter(state, resourceUid) {
            if (!resourceUid) {
                return;
            }
            const list = state.listOfFilters || [];
            const index = list.findIndex((filter: any) => filter.resourceUid === resourceUid);
            if (index !== -1) {
                list.splice(index, 1);
                state.listOfFilters = [...list];
            }
        },
        setSelectedFilter(state, payload) {
            state.selectedFilter = payload;
            state.showEditMode = false;
            
        },
        editSelectedFilter(state, payload) {
            state.selectedFilter = payload;
            state.showEditMode = true;
        },
        setIsUpdateButton(state , payload) {
            state.isUpdateButton = payload;
        },
        setUpdatedFilter(state , payload) {
            state.selectedFilter = payload;
        },
        setIsLoading(state, payload){
            state.isLoading = payload ?? false;
        },
        setLastLoadTimestamp(state, payload) {
            state.lastLoadTimestamp = payload ?? 0;
        },
        resetState(state) {
            state.listOfFilters = [];
            state.ShowFilter = false;
            state.showEditMode = false;
            state.isUpdateButton = false;
            state.selectedFilter = undefined;
            state.isLoading = false;
            state.lastLoadTimestamp = 0;
            clearFilterCountCache();
        }
    }
}
export default filterModule;
